import { useEffect, useState, useCallback, useRef } from 'react';
import Layout from '../../components/Layout';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import { todayISO, fmtDateTimeNum } from '../../utils/dateFormat';
import { confirm } from '../../components/ConfirmDialog';

const fmt = (v) => Number(v || 0).toFixed(2);
const inputCls = 'w-full border-2 border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-blue-400 transition-all';

// ── Reason Combobox ───────────────────────────────────────────────────────────
function ReasonCombobox({ value, onChange, presets, onAddPreset, onRemovePreset }) {
    const [query, setQuery] = useState('');
    const [open,  setOpen]  = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    const filtered = presets.filter(p => p.toLowerCase().includes(query.toLowerCase()));
    const canAdd   = query.trim() && !presets.map(p => p.toLowerCase()).includes(query.trim().toLowerCase());

    return (
        <div ref={ref} className="relative">
            <div
                className={`${inputCls} flex items-center justify-between cursor-pointer select-none ${value ? 'text-slate-800 font-semibold' : 'text-slate-400'}`}
                onClick={() => { setOpen(o => !o); setQuery(''); }}
            >
                <span className="truncate">{value || 'Seçin...'}</span>
                <span className="text-slate-400 ml-2 shrink-0">{open ? '▴' : '▾'}</span>
            </div>

            {open && (
                <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
                    <div className="p-2 border-b border-slate-100">
                        <input
                            autoFocus type="text" value={query}
                            onChange={e => setQuery(e.target.value)}
                            placeholder="Axtar..."
                            className="w-full text-sm px-3 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-blue-400"
                        />
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                        {filtered.length === 0 && !canAdd ? (
                            <div className="text-center text-xs text-slate-400 py-4">Nəticə yoxdur</div>
                        ) : filtered.map(p => (
                            <div key={p} className={`flex items-center px-3 py-2 hover:bg-blue-50 ${value === p ? 'bg-blue-50' : ''}`}>
                                <span
                                    className={`flex-1 text-sm cursor-pointer ${value === p ? 'font-semibold text-blue-700' : 'text-slate-700'}`}
                                    onClick={() => { onChange(p); setOpen(false); setQuery(''); }}
                                >{p}</span>
                                <button type="button"
                                    onClick={e => { e.stopPropagation(); onRemovePreset(p); if (value === p) onChange(''); }}
                                    className="text-slate-300 hover:text-red-400 text-xs ml-2 shrink-0">✕</button>
                            </div>
                        ))}
                    </div>
                    {canAdd && (
                        <div className="border-t border-slate-100 p-2">
                            <button type="button"
                                onClick={() => { const t = query.trim(); onAddPreset(t); onChange(t); setOpen(false); setQuery(''); }}
                                className="w-full flex items-center gap-1.5 px-2 py-1.5 text-sm font-semibold text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                            >
                                <span className="text-base leading-none">+</span>
                                <span>"{query.trim()}" əlavə et</span>
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ── Preset management strip ───────────────────────────────────────────────────
function PresetManager({ label, presets, onAdd, onRemove, color = 'blue' }) {
    const [newVal, setNewVal] = useState('');
    const colors = {
        amber:  { btn: 'bg-amber-500 hover:bg-amber-600',  badge: 'bg-amber-100 text-amber-700' },
        purple: { btn: 'bg-purple-500 hover:bg-purple-600', badge: 'bg-purple-100 text-purple-700' },
    }[color] || { btn: 'bg-blue-600 hover:bg-blue-700', badge: 'bg-slate-100 text-slate-700' };

    return (
        <div>
            <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">{label}</p>
            <div className="flex gap-2 mb-2">
                <input
                    type="text" value={newVal}
                    onChange={e => setNewVal(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onAdd(newVal); setNewVal(''); } }}
                    placeholder="Yeni xərc növü..."
                    className="flex-1 border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400"
                />
                <button type="button"
                    disabled={!newVal.trim()}
                    onClick={() => { onAdd(newVal); setNewVal(''); }}
                    className={`px-3 py-1.5 text-white text-xs font-bold rounded-lg disabled:opacity-40 transition-all ${colors.btn}`}
                >+ Əlavə</button>
            </div>
            {presets.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Hələ əlavə edilməyib</p>
            ) : (
                <div className="flex flex-wrap gap-1.5">
                    {presets.map(p => (
                        <span key={p} className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${colors.badge}`}>
                            {p}
                            <button type="button" onClick={() => onRemove(p)}
                                className="opacity-50 hover:opacity-100 leading-none">✕</button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Ofis panel (DB ofis_no=2, receives from Kassa) ────────────────────────────
function OfisPanel1({ summary, onRefresh }) {
    const balance = Number(summary?.ofis2_balance || 0);

    const [subtab,   setSubtab]   = useState('xerc'); // 'xerc' | 'transfer' | 'presets'
    const [presets,  setPresets]  = useState([]);
    const [expForm,  setExpForm]  = useState({ amount: '', reason: '' });
    const [expSaving,setExpSaving]= useState(false);
    const [intForm,  setIntForm]  = useState({ amount: '', note: '' });
    const [intSaving,setIntSaving]= useState(false);
    const [records,    setRecords]    = useState([]);
    const [loadingRec, setLoadingRec] = useState(false);
    const [recDate,    setRecDate]    = useState(todayISO());

    // Xərc adları serverdən yüklənir (bütün kompüterlərdə paylaşılır)
    const loadPresetsSrv = useCallback(async () => {
        try { const res = await api.get('/kassa-ledger/ofis-expense-presets?ofis_no=2'); setPresets(res.data.data || []); } catch { /* ignore */ }
    }, []);
    useEffect(() => { loadPresetsSrv(); }, [loadPresetsSrv]);

    const addPreset = async (name) => {
        const t = name.trim();
        if (!t || presets.map(p => p.toLowerCase()).includes(t.toLowerCase())) return;
        setPresets(prev => [...prev, t]);
        try { await api.post('/kassa-ledger/ofis-expense-presets', { ofis_no: 2, name: t }); } catch { /* ignore */ }
    };
    const removePreset = async (name) => {
        setPresets(prev => prev.filter(p => p !== name));
        try { await api.delete('/kassa-ledger/ofis-expense-presets', { data: { ofis_no: 2, name } }); } catch { /* ignore */ }
    };

    const loadRecords = useCallback(async () => {
        setLoadingRec(true);
        try { const res = await api.get(`/kassa-ledger/ofis-expenses?ofis_no=2&from=${recDate}&to=${recDate}`); setRecords(res.data.data || []); }
        catch {} finally { setLoadingRec(false); }
    }, [recDate]);

    useEffect(() => { loadRecords(); }, [loadRecords]);

    const handleExpense = async (e) => {
        e.preventDefault();
        const amt = Number(expForm.amount);
        if (!amt || amt <= 0) return toast.error('Məbləğ daxil edin.');
        if (!expForm.reason) return toast.error('Xərc adı seçin.');
        if (amt > balance) return toast.error(`Ofis-də cəmi ${fmt(balance)} ₼ var.`);
        setExpSaving(true);
        try {
            await api.post('/kassa-ledger/ofis-expense', { ofis_no: 2, amount: amt, reason: expForm.reason, date: todayISO() });
            toast.success('Xərc qeyd edildi.');
            setExpForm({ amount: '', reason: '' });
            onRefresh(); loadRecords();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta.'); }
        finally { setExpSaving(false); }
    };

    const handleIntTransfer = async (e) => {
        e.preventDefault();
        const amt = Number(intForm.amount);
        if (!amt || amt <= 0) return toast.error('Məbləğ daxil edin.');
        if (amt > balance) return toast.error(`Ofis-də cəmi ${fmt(balance)} ₼ var.`);
        setIntSaving(true);
        try {
            await api.post('/kassa-ledger/ofis-internal-transfer', { amount: amt, note: intForm.note, date: todayISO() });
            toast.success(`${fmt(amt)} ₼ Ofis - 2-ə köçürüldü.`);
            setIntForm({ amount: '', note: '' }); onRefresh();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta.'); }
        finally { setIntSaving(false); }
    };

    const handleDeleteRecord = async (id) => {
        if (!(await confirm('Bu xərc silinsin?'))) return;
        try { await api.delete(`/kassa-ledger/ofis-expenses/${id}`); toast.success('Silindi.'); onRefresh(); loadRecords(); }
        catch { toast.error('Silinmədi.'); }
    };

    return (
        <div className="bg-white rounded-2xl border border-amber-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-amber-100 flex items-center justify-between bg-amber-50">
                <div>
                    <h2 className="font-bold text-amber-800 text-base">🏢 Ofis</h2>
                    <p className="text-xs text-amber-500 mt-0.5">Kassadan köçürülən pul</p>
                </div>
                <div className="rounded-xl bg-white border border-amber-200 px-4 py-2 text-right">
                    <p className="text-[10px] font-semibold text-amber-400 uppercase tracking-wide">Qalıq</p>
                    <p className="text-2xl font-black text-amber-700">{fmt(balance)} ₼</p>
                </div>
            </div>

            {/* Alt-menyu (tab) */}
            <div className="flex gap-1.5 px-4 pt-4">
                {[
                    { k: 'xerc',     label: '💸 Xərc' },
                    { k: 'transfer', label: '→ Ofis-2' },
                    { k: 'presets',  label: '⚙️ Növlər' },
                ].map(t => (
                    <button key={t.k} type="button" onClick={() => setSubtab(t.k)}
                        className={`flex-1 px-2 py-2 rounded-xl text-xs font-bold transition-all ${
                            subtab === t.k ? 'bg-amber-500 text-white shadow-sm' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        }`}>
                        {t.label}
                    </button>
                ))}
            </div>

            <div className="p-5">

                {/* Xərc növləri */}
                {subtab === 'presets' && (
                    <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                        <PresetManager label="Ofis — Xərc növləri" presets={presets} onAdd={addPreset} onRemove={removePreset} color="amber" />
                    </div>
                )}

                {/* Ofis - 2-ə köçür */}
                {subtab === 'transfer' && (
                    <div className="rounded-xl bg-purple-50 border border-purple-100 p-4">
                        <h3 className="text-xs font-bold text-purple-600 uppercase tracking-wide mb-3">→ Ofis - 2-ə köçür</h3>
                        <form onSubmit={handleIntTransfer} className="space-y-3">
                            <div>
                                <label className="text-[11px] font-semibold text-purple-600 uppercase tracking-wide mb-1 block">Məbləğ (₼)</label>
                                <input type="number" min="0.01" step="0.01"
                                    value={intForm.amount}
                                    onChange={e => setIntForm(p => ({ ...p, amount: e.target.value }))}
                                    placeholder="0.00"
                                    className={`${inputCls} font-bold text-lg text-purple-700 ${intForm.amount && Number(intForm.amount) > balance ? 'border-red-400 bg-red-50' : 'border-purple-200 focus:border-purple-400'}`}
                                />
                                {intForm.amount && Number(intForm.amount) > balance && (
                                    <p className="text-[11px] text-red-500 mt-1">Maks: {fmt(balance)} ₼</p>
                                )}
                            </div>
                            <div>
                                <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Qeyd (istəyə bağlı)</label>
                                <input type="text" value={intForm.note}
                                    onChange={e => setIntForm(p => ({ ...p, note: e.target.value }))}
                                    placeholder="Məs: aylıq köçürmə"
                                    className={inputCls} />
                            </div>
                            <button type="submit"
                                disabled={intSaving || !intForm.amount || Number(intForm.amount) <= 0 || Number(intForm.amount) > balance}
                                className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white py-2.5 rounded-xl text-sm font-bold transition-all">
                                {intSaving ? 'Köçürülür...' : '→ Ofis - 2-ə göndər'}
                            </button>
                        </form>
                    </div>
                )}

                {/* Xərc + tarixi */}
                {subtab === 'xerc' && (
                    <div className="space-y-5">
                        <div>
                            <h3 className="text-xs font-bold text-red-500 uppercase tracking-wide mb-3">− Ofis Xərcləmə</h3>
                            <form onSubmit={handleExpense} className="space-y-3">
                                <div>
                                    <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Xərc adı *</label>
                                    <ReasonCombobox
                                        value={expForm.reason}
                                        onChange={v => setExpForm(p => ({ ...p, reason: v }))}
                                        presets={presets} onAddPreset={addPreset} onRemovePreset={removePreset}
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] font-semibold text-red-500 uppercase tracking-wide mb-1 block">Məbləğ (₼)</label>
                                    <input type="number" min="0.01" step="0.01"
                                        value={expForm.amount}
                                        onChange={e => setExpForm(p => ({ ...p, amount: e.target.value }))}
                                        placeholder="0.00"
                                        className={`${inputCls} font-bold text-lg text-red-600 ${expForm.amount && Number(expForm.amount) > balance ? 'border-red-400 bg-red-50' : ''}`}
                                    />
                                    {expForm.amount && Number(expForm.amount) > balance && (
                                        <p className="text-[11px] text-red-500 mt-1">Maks: {fmt(balance)} ₼</p>
                                    )}
                                </div>
                                <button type="submit"
                                    disabled={expSaving || !expForm.amount || Number(expForm.amount) <= 0 || !expForm.reason || Number(expForm.amount) > balance}
                                    className="w-full bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white py-2.5 rounded-xl text-sm font-bold transition-all">
                                    {expSaving ? 'Saxlanılır...' : '− Xərc əlavə et'}
                                </button>
                            </form>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide">Xərclər tarixi</h3>
                                <input type="date" value={recDate} onChange={e => setRecDate(e.target.value)}
                                    className="border border-slate-200 rounded-lg px-2 py-1 text-xs focus:outline-none" />
                            </div>
                            {loadingRec
                                ? <div className="text-center text-xs text-slate-400 py-3">Yüklənir...</div>
                                : records.length === 0
                                    ? <div className="text-center text-xs text-slate-300 py-3">Xərc yoxdur.</div>
                                    : <div className="space-y-1.5 max-h-40 overflow-y-auto">
                                        {records.map(r => (
                                            <div key={r.id} className="flex items-center justify-between bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                                                <div>
                                                    <p className="text-sm font-semibold text-slate-700">{r.reason}</p>
                                                    <p className="text-[11px] text-slate-400">{fmtDateTimeNum(r.created_at)}</p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-black text-red-600 text-sm">−{fmt(r.amount)} ₼</span>
                                                    <button onClick={() => handleDeleteRecord(r.id)} className="text-red-300 hover:text-red-500 text-xs">✕</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                            }
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Ofis - 2 panel (DB ofis_no=3, receives from Ofis) ────────────────────────
function OfisPanel2({ summary, onRefresh }) {
    const balance = Number(summary?.ofis3_balance || 0);

    const [subtab,   setSubtab]   = useState('presets'); // 'presets' | 'xerc' — növlər əvvəl, xərcləmə sonra
    const [presets,  setPresets]  = useState([]);
    const [expForm,  setExpForm]  = useState({ amount: '', reason: '' });
    const [expSaving,setExpSaving]= useState(false);
    const [records,    setRecords]    = useState([]);
    const [loadingRec, setLoadingRec] = useState(false);
    const [recDate,    setRecDate]    = useState(todayISO());

    // Xərc adları serverdən yüklənir (bütün kompüterlərdə paylaşılır)
    const loadPresetsSrv = useCallback(async () => {
        try { const res = await api.get('/kassa-ledger/ofis-expense-presets?ofis_no=3'); setPresets(res.data.data || []); } catch { /* ignore */ }
    }, []);
    useEffect(() => { loadPresetsSrv(); }, [loadPresetsSrv]);

    const addPreset = async (name) => {
        const t = name.trim();
        if (!t || presets.map(p => p.toLowerCase()).includes(t.toLowerCase())) return;
        setPresets(prev => [...prev, t]);
        try { await api.post('/kassa-ledger/ofis-expense-presets', { ofis_no: 3, name: t }); } catch { /* ignore */ }
    };
    const removePreset = async (name) => {
        setPresets(prev => prev.filter(p => p !== name));
        try { await api.delete('/kassa-ledger/ofis-expense-presets', { data: { ofis_no: 3, name } }); } catch { /* ignore */ }
    };

    const loadRecords = useCallback(async () => {
        setLoadingRec(true);
        try { const res = await api.get(`/kassa-ledger/ofis-expenses?ofis_no=3&from=${recDate}&to=${recDate}`); setRecords(res.data.data || []); }
        catch {} finally { setLoadingRec(false); }
    }, [recDate]);

    useEffect(() => { loadRecords(); }, [loadRecords]);

    const handleExpense = async (e) => {
        e.preventDefault();
        const amt = Number(expForm.amount);
        if (!amt || amt <= 0) return toast.error('Məbləğ daxil edin.');
        if (!expForm.reason) return toast.error('Xərc adı seçin.');
        if (amt > balance) return toast.error(`Ofis - 2-də cəmi ${fmt(balance)} ₼ var.`);
        setExpSaving(true);
        try {
            await api.post('/kassa-ledger/ofis-expense', { ofis_no: 3, amount: amt, reason: expForm.reason, date: todayISO() });
            toast.success('Xərc qeyd edildi.');
            setExpForm({ amount: '', reason: '' });
            onRefresh(); loadRecords();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta.'); }
        finally { setExpSaving(false); }
    };

    const handleDeleteRecord = async (id) => {
        if (!(await confirm('Bu xərc silinsin?'))) return;
        try { await api.delete(`/kassa-ledger/ofis-expenses/${id}`); toast.success('Silindi.'); onRefresh(); loadRecords(); }
        catch { toast.error('Silinmədi.'); }
    };

    return (
        <div className="bg-white rounded-2xl border border-purple-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-purple-100 flex items-center justify-between bg-purple-50">
                <div>
                    <h2 className="font-bold text-purple-800 text-base">🗄️ Ofis - 2</h2>
                    <p className="text-xs text-purple-400 mt-0.5">Ofis-dən köçürülən pul</p>
                </div>
                <div className="rounded-xl bg-white border border-purple-200 px-4 py-2 text-right">
                    <p className="text-[10px] font-semibold text-purple-400 uppercase tracking-wide">Qalıq</p>
                    <p className="text-2xl font-black text-purple-700">{fmt(balance)} ₼</p>
                </div>
            </div>

            {/* Alt-menyu (tab) */}
            <div className="flex gap-1.5 px-4 pt-4">
                {[
                    { k: 'presets', label: '⚙️ Xərc növləri' },
                    { k: 'xerc',    label: '💸 Xərcləmə' },
                ].map(t => (
                    <button key={t.k} type="button" onClick={() => setSubtab(t.k)}
                        className={`flex-1 px-2 py-2 rounded-xl text-xs font-bold transition-all ${
                            subtab === t.k ? 'bg-purple-500 text-white shadow-sm' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                        }`}>
                        {t.label}
                    </button>
                ))}
            </div>

            <div className="p-5">

                {/* Xərc növləri */}
                {subtab === 'presets' && (
                    <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                        <PresetManager label="Ofis - 2 — Xərc növləri" presets={presets} onAdd={addPreset} onRemove={removePreset} color="purple" />
                    </div>
                )}

                {/* Xərc + tarixi */}
                {subtab === 'xerc' && (
                    <div className="space-y-5">
                        <div>
                            <h3 className="text-xs font-bold text-red-500 uppercase tracking-wide mb-3">− Ofis - 2 Xərcləmə</h3>
                            <form onSubmit={handleExpense} className="space-y-3">
                                <div>
                                    <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Xərc adı *</label>
                                    <ReasonCombobox
                                        value={expForm.reason}
                                        onChange={v => setExpForm(p => ({ ...p, reason: v }))}
                                        presets={presets} onAddPreset={addPreset} onRemovePreset={removePreset}
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] font-semibold text-red-500 uppercase tracking-wide mb-1 block">Məbləğ (₼)</label>
                                    <input type="number" min="0.01" step="0.01"
                                        value={expForm.amount}
                                        onChange={e => setExpForm(p => ({ ...p, amount: e.target.value }))}
                                        placeholder="0.00"
                                        className={`${inputCls} font-bold text-lg text-red-600 ${expForm.amount && Number(expForm.amount) > balance ? 'border-red-400 bg-red-50' : ''}`}
                                    />
                                    {expForm.amount && Number(expForm.amount) > balance && (
                                        <p className="text-[11px] text-red-500 mt-1">Maks: {fmt(balance)} ₼</p>
                                    )}
                                </div>
                                <button type="submit"
                                    disabled={expSaving || !expForm.amount || Number(expForm.amount) <= 0 || !expForm.reason || Number(expForm.amount) > balance}
                                    className="w-full bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white py-2.5 rounded-xl text-sm font-bold transition-all">
                                    {expSaving ? 'Saxlanılır...' : '− Xərc əlavə et'}
                                </button>
                            </form>
                        </div>

                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wide">Xərclər tarixi</h3>
                                <input type="date" value={recDate} onChange={e => setRecDate(e.target.value)}
                                    className="border border-slate-200 rounded-lg px-2 py-1 text-xs focus:outline-none" />
                            </div>
                            {loadingRec
                                ? <div className="text-center text-xs text-slate-400 py-3">Yüklənir...</div>
                                : records.length === 0
                                    ? <div className="text-center text-xs text-slate-300 py-3">Xərc yoxdur.</div>
                                    : <div className="space-y-1.5 max-h-40 overflow-y-auto">
                                        {records.map(r => (
                                            <div key={r.id} className="flex items-center justify-between bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                                                <div>
                                                    <p className="text-sm font-semibold text-slate-700">{r.reason}</p>
                                                    <p className="text-[11px] text-slate-400">{fmtDateTimeNum(r.created_at)}</p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-black text-red-600 text-sm">−{fmt(r.amount)} ₼</span>
                                                    <button onClick={() => handleDeleteRecord(r.id)} className="text-red-300 hover:text-red-500 text-xs">✕</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                            }
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────
// ── Edit Ofis Balance Modal ───────────────────────────────────
function EditOfisModal({ data, onClose, onSaved }) {
    const [amount, setAmount] = useState(String(data.current?.toFixed(2) || ''));
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);

    const save = async () => {
        const n = Number(amount);
        if (isNaN(n)) return toast.error('Düzgün məbləğ daxil edin.');
        setSaving(true);
        try {
            await api.post('/kassa-ledger/set-ofis-balance', {
                ofis_no: data.no,
                new_balance: n,
                reason: reason || 'Manual düzəliş'
            });
            toast.success('Balans yeniləndi.');
            onSaved();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta.');
        } finally { setSaving(false); }
    };

    const label = data.no === 2 ? 'Ofis' : data.no === 3 ? 'Ofis - 2' : 'Ofis';

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl">
                <h2 className="font-bold text-lg text-slate-800 mb-1">🏦 {label} balansını düzəlt</h2>
                <p className="text-sm text-slate-400 mb-4">Cari balans: <b className="text-slate-700">{Number(data.current).toFixed(2)} ₼</b></p>

                <label className="block text-xs font-semibold text-slate-600 mb-1">Yeni balans (₼)</label>
                <input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
                    className="w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 mb-3" />

                <label className="block text-xs font-semibold text-slate-600 mb-1">Səbəb (ixtiyari)</label>
                <input type="text" value={reason} onChange={e => setReason(e.target.value)}
                    placeholder="Məs: köhnə sistemdən başlanğıc balans"
                    className="w-full border-2 border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 mb-4" />

                <div className="flex gap-2">
                    <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600">Ləğv et</button>
                    <button onClick={save} disabled={saving}
                        className="flex-1 py-2.5 rounded-xl bg-amber-600 text-white text-sm font-bold disabled:opacity-50">
                        {saving ? '...' : '💾 Saxla'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function OfisPanel() {
    const [summary,        setSummary] = useState(null);
    const [loadingSummary, setLoading] = useState(true);
    const [editOfis,       setEditOfis] = useState(null);

    const loadSummary = useCallback(async () => {
        setLoading(true);
        try { const res = await api.get('/kassa-ledger/ofis-summary'); setSummary(res.data); }
        catch {} finally { setLoading(false); }
    }, []);

    useEffect(() => { loadSummary(); }, [loadSummary]);

    return (
        <Layout>
            <div className="max-w-5xl mx-auto p-4 space-y-6">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">🏢 Ofis İdarəsi</h1>
                    <p className="text-sm text-slate-400 mt-1">Kassir: Kassa → Ofis &nbsp;|&nbsp; Admin: Ofis → Ofis - 2 / Xərclər</p>
                </div>

                {/* Balance strip */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 text-center">
                        <p className="text-[11px] font-bold text-blue-400 uppercase tracking-wide mb-1">Kassada var</p>
                        <p className="text-xl sm:text-2xl font-black text-blue-700 whitespace-nowrap">{fmt(summary?.kassa_balance)} ₼</p>
                    </div>
                    <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 text-center relative">
                        <p className="text-[11px] font-bold text-amber-500 uppercase tracking-wide mb-1 px-14 sm:px-0">Ofis Qalıq</p>
                        <p className="text-xl sm:text-2xl font-black text-amber-700 whitespace-nowrap">{fmt(summary?.ofis2_balance)} ₼</p>
                        <button onClick={() => setEditOfis({ no: 2, current: summary?.ofis2_balance || 0 })}
                            className="absolute top-2 right-2 text-amber-600 hover:bg-amber-100 rounded-lg px-2 py-0.5 text-xs font-bold">✎ Düzəlt</button>
                    </div>
                    <div className="bg-purple-50 border border-purple-100 rounded-2xl p-4 text-center relative">
                        <p className="text-[11px] font-bold text-purple-500 uppercase tracking-wide mb-1 px-14 sm:px-0">Ofis - 2 Qalıq</p>
                        <p className="text-xl sm:text-2xl font-black text-purple-700 whitespace-nowrap">{fmt(summary?.ofis3_balance)} ₼</p>
                        <button onClick={() => setEditOfis({ no: 3, current: summary?.ofis3_balance || 0 })}
                            className="absolute top-2 right-2 text-purple-600 hover:bg-purple-100 rounded-lg px-2 py-0.5 text-xs font-bold">✎ Düzəlt</button>
                    </div>
                </div>

                {/* Edit modal */}
                {editOfis && <EditOfisModal data={editOfis} onClose={() => setEditOfis(null)} onSaved={loadSummary} />}

                {loadingSummary ? (
                    <div className="text-center text-slate-400 py-10">Yüklənir...</div>
                ) : (
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                        <OfisPanel1 summary={summary} onRefresh={loadSummary} />
                        <OfisPanel2 summary={summary} onRefresh={loadSummary} />
                    </div>
                )}
            </div>
        </Layout>
    );
}
